import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getFilesTool from 'common/src/modules/shared/copilot/tools/getFilesTool';
import analyzeExcelHeadersTool from 'common/src/modules/shared/copilot/tools/analyzeExcelHeadersTool/analyzeExcelHeadersTool';
import readValuesFromExcelTool from '../../../../tools/general/readValuesFromExcelTool/readValuesFromExcelTool';
import deleteOutuputsTool from '../general/deleteOutputsTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import {ScheduleCardScratchpadState} from '../../scratchpad/scheduleCardScratchpad.types';
import getOrCreateScheduleCard from '../../utils/schedule/getOrCreateScheduleCard';
import setScheduleTypeTool from './setScheduleTypeTool';
import setTimezoneSourceTool from './setTimezoneSourceTool';
import setTimeZoneTool from './setTimeZoneTool';
import setRandomizeOutputsTool from './setRandomizeOutputsTool';
import addScheduleOutputTool from './addScheduleOutputTool';
import editScheduleOutputTool from './editScheduleOutputTool';
import {FallbackOutputGuidelines} from '../../promptToCardAgentV2.consts';

const cardSpecificInstructions = `### What is a Schedule card
A Schedule card routes traffic based on time — either date ranges or days of the week with time-of-day windows. The fallback output is selected when the current time does not match any regular output's schedule.

### Schedule card rules
- When dealing with date ranges, and the user did not specify time of day, use the time of day 00:00:00 to 23:59:59.
${FallbackOutputGuidelines}.
- DO NOT add fallback outputs; only set the outputs requested by the user.

### Property Dependencies
- The schedule type (date range vs days of week) determines the format of outputs — outputs must match the current schedule type. Changing the schedule type clears all existing regular outputs.
- The timezone is only editable when the timezone source is set to Manual, so the timezone source must be set before the timezone value.
- Randomize outputs and card name/description have no dependencies on other properties.
- No investigation/search phase is needed — all properties can be configured directly.`;

export default function addOrUpdateScheduleCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, ScheduleCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId, card} = await preProcessStandaloneAgent(
      params,
      store,
      getOrCreateScheduleCard,
    );

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateScheduleCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.SCHEDULE]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.SCHEDULE,
        cardId: normalizedCardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getFilesTool(),
        getCardDetailsTool(),
        analyzeExcelHeadersTool(),
        readValuesFromExcelTool(),
        deleteOutuputsTool(),

        setScheduleTypeTool(),
        setTimezoneSourceTool(),
        setTimeZoneTool(),
        setRandomizeOutputsTool(),
        addScheduleOutputTool(),
        editScheduleOutputTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId: normalizedCardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateScheduleCard,
    description:
      'Add or update a Schedule card. Card selects an output based on time — routing traffic to different outputs by date range or day-of-week/time range, with configurable timezone (Manual, User, or Advertiser).',
    displayName: 'Working on schedule card',
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
